import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Heart,
  Search,
  Bell,
  User,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { Button } from '../ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/DropdownMenu';
import { useAuthStore } from '../../stores/auth';
import { api } from '../../services/api';
import { toast } from '../ui/Toast';
import { cn } from '../../lib/utils';

const NAV_ITEMS = [
  { href: '/', label: 'Home', icon: Home },
  { href: '/listings', label: 'Annunci', icon: Search },
];

const AUTH_NAV_ITEMS = [
  { href: '/favorites', label: 'Preferiti', icon: Heart },
  { href: '/searches', label: 'Alert & Lead', icon: Bell },
];

export function Header() {
  const { isAuthenticated, user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch {
      // Ignore errors
    }
    logout();
    toast({ title: 'Logout effettuato', variant: 'default' });
    navigate('/');
  };

  return (
    <header
      className="sticky top-0 w-full border-b glass safe-top"
      style={{
        zIndex: 'var(--z-sticky)',
        height: 'var(--header-height)',
        borderColor: 'var(--color-border-subtle)',
      }}
    >
      <div className="container mx-auto h-full" style={{ padding: '0 var(--spacing-md)' }}>
        <div className="flex h-full items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center transition-opacity hover:opacity-80"
            style={{ gap: 'var(--spacing-sm)' }}
          >
            <img
              src="/logo-icon.svg"
              alt="HomeFinder"
              className="h-9 w-9"
              style={{ borderRadius: 'var(--radius-sm)' }}
            />
            <span
              className="hidden sm:block"
              style={{
                fontSize: 'var(--font-size-xl)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-text-primary)',
                letterSpacing: 'var(--letter-spacing-tight)',
              }}
            >
              HomeFinder
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center" style={{ gap: 'var(--spacing-xs)' }}>
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center transition-all',
                  location.pathname === item.href
                    ? 'nav-link-active'
                    : 'nav-link'
                )}
                style={{
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  borderRadius: 'var(--radius-sm)',
                  color: location.pathname === item.href
                    ? 'var(--color-accent)'
                    : 'var(--color-text-secondary)',
                  backgroundColor: location.pathname === item.href
                    ? 'var(--color-accent-50)'
                    : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (location.pathname !== item.href) {
                    e.currentTarget.style.color = 'var(--color-text-primary)';
                    e.currentTarget.style.backgroundColor = 'var(--color-surface-secondary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== item.href) {
                    e.currentTarget.style.color = 'var(--color-text-secondary)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <item.icon style={{ marginRight: 'var(--spacing-sm)', width: '16px', height: '16px' }} />
                {item.label}
              </Link>
            ))}

            {isAuthenticated &&
              AUTH_NAV_ITEMS.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex items-center transition-all',
                    location.pathname === item.href
                      ? 'nav-link-active'
                      : 'nav-link'
                  )}
                  style={{
                    padding: 'var(--spacing-sm) var(--spacing-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    borderRadius: 'var(--radius-sm)',
                    color: location.pathname === item.href
                      ? 'var(--color-accent)'
                      : 'var(--color-text-secondary)',
                    backgroundColor: location.pathname === item.href
                      ? 'var(--color-accent-50)'
                      : 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    if (location.pathname !== item.href) {
                      e.currentTarget.style.color = 'var(--color-text-primary)';
                      e.currentTarget.style.backgroundColor = 'var(--color-surface-secondary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (location.pathname !== item.href) {
                      e.currentTarget.style.color = 'var(--color-text-secondary)';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  <item.icon style={{ marginRight: 'var(--spacing-sm)', width: '16px', height: '16px' }} />
                  {item.label}
                </Link>
              ))}
          </nav>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center" style={{ gap: 'var(--spacing-sm)' }}>
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="relative rounded-full"
                    style={{
                      width: '44px',
                      height: '44px',
                      minWidth: '44px',
                      minHeight: '44px',
                    }}
                    aria-label="Menu utente"
                  >
                    <div
                      className="flex items-center justify-center rounded-full"
                      style={{
                        width: '36px',
                        height: '36px',
                        backgroundColor: 'var(--color-surface-secondary)',
                      }}
                    >
                      <User
                        style={{
                          width: '20px',
                          height: '20px',
                          color: 'var(--color-text-secondary)',
                        }}
                      />
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div
                    className="flex items-center justify-start"
                    style={{ gap: 'var(--spacing-sm)', padding: 'var(--spacing-sm)' }}
                  >
                    <div className="flex flex-col leading-none" style={{ gap: 'var(--spacing-xs)' }}>
                      <p style={{ fontWeight: 'var(--font-weight-medium)' }}>{user?.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/favorites" className="w-full cursor-pointer">
                      <Heart style={{ marginRight: 'var(--spacing-sm)', width: '16px', height: '16px' }} />
                      Preferiti
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/searches" className="w-full cursor-pointer">
                      <Bell style={{ marginRight: 'var(--spacing-sm)', width: '16px', height: '16px' }} />
                      Alert & Lead
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                    <LogOut style={{ marginRight: 'var(--spacing-sm)', width: '16px', height: '16px' }} />
                    Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Button variant="ghost" asChild>
                  <Link to="/login">Accedi</Link>
                </Button>
                <Button asChild>
                  <Link to="/register">Registrati</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden flex items-center justify-center transition-colors"
            style={{
              minWidth: '44px',
              minHeight: '44px',
              padding: 'var(--spacing-sm)',
              color: 'var(--color-text-secondary)',
              borderRadius: 'var(--radius-sm)',
            }}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = 'var(--color-text-primary)';
              e.currentTarget.style.backgroundColor = 'var(--color-surface-secondary)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = 'var(--color-text-secondary)';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
            aria-label={mobileMenuOpen ? 'Chiudi menu' : 'Apri menu'}
          >
            {mobileMenuOpen ? (
              <X style={{ width: '24px', height: '24px' }} />
            ) : (
              <Menu style={{ width: '24px', height: '24px' }} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div
          className="md:hidden"
          style={{
            borderTop: '1px solid var(--color-border-subtle)',
            backgroundColor: 'var(--color-surface-primary)',
          }}
        >
          <nav
            className="container mx-auto"
            style={{ padding: 'var(--spacing-md)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}
          >
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center transition-colors"
                style={{
                  padding: 'var(--spacing-sm) var(--spacing-md)',
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-medium)',
                  borderRadius: 'var(--radius-sm)',
                  color: location.pathname === item.href
                    ? 'var(--color-accent)'
                    : 'var(--color-text-secondary)',
                  backgroundColor: location.pathname === item.href
                    ? 'var(--color-accent-50)'
                    : 'transparent',
                }}
              >
                <item.icon style={{ marginRight: 'var(--spacing-sm)', width: '20px', height: '20px' }} />
                {item.label}
              </Link>
            ))}

            {isAuthenticated && (
              <>
                {AUTH_NAV_ITEMS.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center transition-colors"
                    style={{
                      padding: 'var(--spacing-sm) var(--spacing-md)',
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 'var(--font-weight-medium)',
                      borderRadius: 'var(--radius-sm)',
                      color: location.pathname === item.href
                        ? 'var(--color-accent)'
                        : 'var(--color-text-secondary)',
                      backgroundColor: location.pathname === item.href
                        ? 'var(--color-accent-50)'
                        : 'transparent',
                    }}
                  >
                    <item.icon style={{ marginRight: 'var(--spacing-sm)', width: '20px', height: '20px' }} />
                    {item.label}
                  </Link>
                ))}
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="flex w-full items-center transition-colors"
                  style={{
                    padding: 'var(--spacing-sm) var(--spacing-md)',
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-medium)',
                    borderRadius: 'var(--radius-sm)',
                    color: 'var(--color-text-secondary)',
                    backgroundColor: 'transparent',
                  }}
                >
                  <LogOut style={{ marginRight: 'var(--spacing-sm)', width: '20px', height: '20px' }} />
                  Esci
                </button>
              </>
            )}

            {!isAuthenticated && (
              <div
                style={{
                  paddingTop: 'var(--spacing-md)',
                  marginTop: 'var(--spacing-sm)',
                  borderTop: '1px solid var(--color-border-subtle)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--spacing-sm)',
                }}
              >
                <Button variant="outline" className="w-full" asChild>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    Accedi
                  </Link>
                </Button>
                <Button className="w-full" asChild>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    Registrati
                  </Link>
                </Button>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
